/**
 * Сервис для работы с Yandex SpeechKit (распознавание речи)
 * Использует серверный API вместо прямых запросов к Yandex
 */

import { VoiceSettings } from '../types';
import API_BASE_URL from '../config/api';

// Токен авторизации для API запросов
let authToken: string | null = null;

async function waitForApiReady(baseUrl: string, timeoutMs: number = 5000, intervalMs: number = 300): Promise<void> {
  const start = Date.now();
  // Быстрая проверка здоровья API перед отправкой крупных данных
  while (Date.now() - start < timeoutMs) {
    try {
      const res = await fetch(`${baseUrl}/api/health`, { method: 'GET' });
      if (res.ok) return;
    } catch (_) {
      // игнорируем, API может быть не готово
    }
    await new Promise(r => setTimeout(r, intervalMs));
  }
}

async function fetchWithRetry(input: RequestInfo | URL, init: RequestInit, attempts: number = 4, baseDelayMs: number = 250): Promise<Response> {
  let lastError: unknown = null;
  for (let i = 0; i < attempts; i++) {
    try {
      // Небольшая задержка перед повтором (экспоненциальный бэк-офф)
      if (i > 0) {
        const delay = baseDelayMs * Math.pow(2, i - 1);
        await new Promise(r => setTimeout(r, delay));
      }
      const res = await fetch(input, init);
      return res;
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError instanceof Error ? lastError : new Error('Не удалось выполнить запрос');
}

interface RecognitionOptions {
  language?: string;
  format?: 'lpcm' | 'oggopus' | 'mp3';
  sampleRateHertz?: number;
}

interface RecognitionResult {
  text: string;
  alternatives?: string[];
}

class YandexSpeechService {
  private audioChunks: ArrayBuffer[] = []; // ArrayBuffer для LPCM данных
  private isRecording = false;
  private stream: MediaStream | null = null;
  private eventListeners: Map<string, ((event: any) => void)[]> = new Map();

  /**
   * Проверить поддержку записи аудио
   */
  isSupported(): boolean {
    return !!(
      navigator.mediaDevices && 
      typeof navigator.mediaDevices.getUserMedia === 'function' && 
      (typeof AudioContext !== 'undefined' || typeof (window as any).webkitAudioContext !== 'undefined')
    );
  }

  /**
   * Начать запись аудио с микрофона
   * Использует Web Audio API для записи в LPCM формате (совместимо с Yandex SpeechKit)
   */
  async startRecording(options: RecognitionOptions = {}): Promise<void> {
    if (!this.isSupported()) {
      throw new Error('Запись аудио не поддерживается в вашем браузере');
    }

    if (this.isRecording) {
      this.stopRecording();
    }

    try {
      const sampleRate = options.sampleRateHertz || 16000;
      
      // Запрашиваем доступ к микрофону
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: sampleRate,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      // Используем Web Audio API для записи в LPCM формате
      // Это гарантирует совместимость с Yandex SpeechKit без конвертации
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({
        sampleRate: sampleRate
      });
      
      const source = audioContext.createMediaStreamSource(this.stream);
      
      // Создаем ScriptProcessorNode для захвата аудио данных
      const bufferSize = 4096;
      const processor = audioContext.createScriptProcessor(bufferSize, 1, 1);
      
      this.audioChunks = [];
      
      processor.onaudioprocess = (event) => {
        if (this.isRecording) {
          const inputData = event.inputBuffer.getChannelData(0);
          // Конвертируем Float32Array в Int16Array (LPCM формат)
          const int16Data = new Int16Array(inputData.length);
          for (let i = 0; i < inputData.length; i++) {
            // Конвертация из float [-1, 1] в int16 [-32768, 32767]
            const s = Math.max(-1, Math.min(1, inputData[i]));
            int16Data[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
          }
          // Сохраняем как ArrayBuffer
          this.audioChunks.push(int16Data.buffer);
        }
      };
      
      source.connect(processor);
      processor.connect(audioContext.destination);
      
      // Сохраняем для остановки
      (this as any).audioContext = audioContext;
      (this as any).processor = processor;
      (this as any).source = source;
      
      this.isRecording = true;
      
      console.log(`Запись начата через Web Audio API, LPCM формат, sampleRate: ${sampleRate}Hz`);

    } catch (error) {
      console.error('Ошибка при начале записи:', error);
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop());
        this.stream = null;
      }
      
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          throw new Error('Доступ к микрофону запрещен. Разрешите использование микрофона в настройках браузера.');
        } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
          throw new Error('Микрофон не найден. Проверьте подключение микрофона.');
        } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
          throw new Error('Микрофон недоступен. Возможно, используется другим приложением.');
        }
      }
      
      throw error;
    }
  }

  /**
   * Остановить запись и отправить на распознавание
   */
  async stopRecordingAndRecognize(options: RecognitionOptions = {}): Promise<string> {
    if (!this.isRecording) {
      throw new Error('Запись не была начата');
    }

    return new Promise(async (resolve, reject) => {
      try {
        // Останавливаем запись (прекращаем захват данных)
        this.isRecording = false;
        
        // Даем немного времени для завершения последних аудио обработок
        await new Promise(resolve => setTimeout(resolve, 100));

        // Объединяем все ArrayBuffer в один LPCM буфер
        const totalLength = this.audioChunks.reduce((sum, chunk) => sum + chunk.byteLength, 0);
        const combinedBuffer = new ArrayBuffer(totalLength);
        const combinedView = new Int16Array(combinedBuffer);
        
        let offset = 0;
        for (const chunk of this.audioChunks) {
          const chunkView = new Int16Array(chunk);
          combinedView.set(chunkView, offset);
          offset += chunkView.length;
        }
        
        if (combinedBuffer.byteLength === 0) {
          reject(new Error('Аудио не записано. Говорите громче.'));
          return;
        }

        console.log('Отправка LPCM аудио на распознавание, размер:', combinedBuffer.byteLength, 'байт');

        // Создаем FormData для отправки LPCM файла
        const formData = new FormData();
        
        // Создаем Blob из LPCM данных
        const lpcmBlob = new Blob([combinedBuffer], { type: 'audio/x-pcm' });
        const audioFile = new File([lpcmBlob], 'recording.pcm', { type: 'audio/x-pcm;bit=16;rate=16000' });
        formData.append('audio', audioFile);

        console.log('Отправка LPCM аудио файла на распознавание:', {
          размер: combinedBuffer.byteLength,
          формат: 'LPCM',
          sampleRate: options.sampleRateHertz || 16000
        });

        // Ждем готовности API (актуально после перезапуска сервера)
        await waitForApiReady(API_BASE_URL, 6000, 300);

        // Отправляем файл на сервер для распознавания через FormData с повторами
        const headers: Record<string, string> = {};
        if (authToken) {
          headers['Authorization'] = `Bearer ${authToken}`;
        }
        
        const response = await fetchWithRetry(
          `${API_BASE_URL}/api/speech/recognize?language=${options.language || 'ru-RU'}&format=lpcm`,
          {
            method: 'POST',
            headers,
            body: formData // FormData автоматически установит правильные заголовки
          },
          4,
          250
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
          throw new Error(errorData.error || `Ошибка сервера: ${response.status}`);
        }

        const result = await response.json();
        
        if (result.success && result.data && result.data.text) {
          console.log('Распознанный текст:', result.data.text);
          resolve(result.data.text);
        } else {
          throw new Error(result.error || 'Текст не распознан');
        }

      } catch (error) {
        console.error('Ошибка при распознавании:', error);
        reject(error);
      } finally {
        // Останавливаем все компоненты Web Audio API
        this.stopRecording();
        this.audioChunks = [];
      }
    });
  }

  /**
   * Остановить запись без распознавания
   */
  stopRecording(): void {
    this.isRecording = false;
    
    // Останавливаем Web Audio API компоненты
    if ((this as any).processor) {
      (this as any).processor.disconnect();
      (this as any).processor = null;
    }
    
    if ((this as any).source) {
      (this as any).source.disconnect();
      (this as any).source = null;
    }
    
    if ((this as any).audioContext) {
      (this as any).audioContext.close().catch(console.error);
      (this as any).audioContext = null;
    }

    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }

    this.audioChunks = [];
  }

  /**
   * Установить токен авторизации
   */
  setAuthToken(token: string | null): void {
    authToken = token;
  }

  /**
   * Проверить статус записи
   */
  isRecordingNow(): boolean {
    return this.isRecording;
  }

  /**
   * Добавить обработчик событий
   */
  addEventListener(eventType: string, callback: (event: any) => void): void {
    if (!this.eventListeners.has(eventType)) {
      this.eventListeners.set(eventType, []);
    }
    this.eventListeners.get(eventType)!.push(callback);
  }

  /**
   * Удалить обработчик событий
   */
  removeEventListener(eventType: string, callback: (event: any) => void): void {
    const listeners = this.eventListeners.get(eventType);
    if (listeners) {
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  /**
   * Отправить событие
   */
  private emitEvent(eventType: string, data: any): void {
    const listeners = this.eventListeners.get(eventType);
    if (listeners) {
      listeners.forEach(callback => callback(data));
    }
  }
}

// Экспортируем единственный экземпляр сервиса
export default new YandexSpeechService();

